import {addDocumentToIndex, createIndex, Index} from "ndx";
import {query, QueryResult} from "ndx-query";
import {Account} from "./model";

export class RecipientIndex {
    private readonly index: Index<Account>;

    constructor(recipients: Account[]) {
        this.index = createIndex(3);

        console.time("Create recipient index");

        for (const recipient of recipients) {
            addDocumentToIndex(
                this.index,
                ACCESSORS,
                whitespaceTokenizer,
                lowerCaseFilter,
                recipient,
                recipient
            );
        }
        console.timeEnd("Create recipient index");
    }

    query(value: string) {
        const results = new Map<Account, QueryResult<Account>>();

        const terms = whitespaceTokenizer(value);

        for (const term of terms) {
            const termResults = query(
                this.index,
                BOOST_FACTORS,
                // BM25 ranking function constants:
                1.2,  // BM25 k1 constant, controls non-linear term frequency normalization (saturation).
                0.75, // BM25 b constant, controls to what degree document length normalizes tf values.
                whitespaceTokenizer,
                lowerCaseFilter,
                undefined,
                term,
            );

            for (const termResult of termResults) {
                const prevResult = results.get(termResult.key);
                results.set(termResult.key, {
                    key: termResult.key,
                    score: termResult.score + (prevResult?.score ?? 0)
                });
            }
        }
        return [...results.values()].sort(scoreComparator);
    }
}

type Accessor = (account: Account) => string;

const ACCESSORS: Accessor[] = [
    account => account.displayAbbr ?? "",
    account => removeDiacritsExceptGermanUmlauts(account.displayName),
    account => removeDiacritsExceptGermanUmlauts(account.displayDescription ?? "")
];

const BOOST_FACTORS = [1, 1, 1];

/**
 * Converts term to lower case.
 *
 * @param term Term.
 * @returns Filtered term.
 */
export function lowerCaseFilter(term: string): string {
    return term.toLowerCase();
}

/**
 * The whitespace tokenizer breaks on whitespace - spaces, tabs, line feeds and assumes that contiguous nonwhitespace
 * characters form a single token.
 *
 * @param s Input string.
 * @returns Array of tokens.
 */
export function whitespaceTokenizer(s: string): string[] {
    return s.trim().split(WS_TOKENIZER_RE);
}

const WS_TOKENIZER_RE = /[\s]+/;


/**
 * Removes diacrits from a string, except german umlauts
 * @param str The string
 */
function removeDiacritsExceptGermanUmlauts(str: string) {
    let result = "";
    for (let i = 0; i < str.length; i++) {
        const char = str.charAt(i);

        // leave german umlauts untouched, remove diacrits from the rest
        if ("äöüÄÖÜ".indexOf(char) >= 0) {
            result += char;
        }
        else {
            result += removeDiacrits(char);
        }
    }
    return result;
}

/**
 * Removes diacrits from a string.
 * @param str The string
 */
function removeDiacrits(str: string) {
    // Thanks, https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
    // Two things are happening here:
    //     normalize()ing to NFD Unicode normal form decomposes combined graphemes into the combination of simple ones. The è of Crème ends up expressed as e + ̀.
    //     Using a regex character class to match the U+0300 → U+036F range, it is now trivial to globally get rid of the diacritics, which the Unicode standard conveniently groups as the Combining Diacritical Marks Unicode block.
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Compares two query results by score.
 * @param a
 * @param b
 */
function scoreComparator(a: QueryResult<Account>, b: QueryResult<Account>) {
    return b.score - a.score;
}
